import { z } from "zod";
import { apiClient } from "../client";
import {
  classificationRuleSchema,
  classificationRuleCategoriesResponseSchema,
} from "./schemas";
import type {
  ClassificationRule,
  CreateClassificationRuleRequest,
  UpdateClassificationRuleRequest,
  AddKeywordsRequest,
  ClassificationRuleCategoriesResponse,
} from "./types";

export async function getClassificationRules(): Promise<ClassificationRule[]> {
  const response = await apiClient
    .get("classification-rules", {})
    .json<ClassificationRule[]>();

  return z.array(classificationRuleSchema).parse(response);
}

export async function getClassificationRule(
  id: string | number
): Promise<ClassificationRule> {
  const response = await apiClient
    .get(`classification-rules/${id}`, {})
    .json<ClassificationRule>();

  return classificationRuleSchema.parse(response);
}

export async function createClassificationRule(
  data: CreateClassificationRuleRequest
): Promise<ClassificationRule> {
  const response = await apiClient
    .post("classification-rules", { json: data })
    .json<ClassificationRule>();

  return classificationRuleSchema.parse(response);
}

export async function updateClassificationRule(
  id: string | number,
  data: UpdateClassificationRuleRequest
): Promise<ClassificationRule> {
  const response = await apiClient
    .put(`classification-rules/${id}`, { json: data })
    .json<ClassificationRule>();

  return classificationRuleSchema.parse(response);
}

export async function deleteClassificationRule(
  id: string | number
): Promise<void> {
  await apiClient.delete(`classification-rules/${id}`, {});
}

export async function addKeywordsToRule(
  id: string | number,
  data: AddKeywordsRequest
): Promise<ClassificationRule> {
  const response = await apiClient
    .post(`classification-rules/${id}/keywords`, { json: data })
    .json<ClassificationRule>();

  return classificationRuleSchema.parse(response);
}

export async function removeKeywordFromRule(
  id: string | number,
  keywordId: string | number
): Promise<void> {
  await apiClient.delete(
    `classification-rules/${id}/keywords/${keywordId}`,
    {}
  );
}

export async function getClassificationRuleTargets(): Promise<string[]> {
  const response = await apiClient
    .get("classification-rules/targets", {})
    .json<string[]>();

  return z.array(z.string()).parse(response);
}

export async function getClassificationRuleCategories(): Promise<ClassificationRuleCategoriesResponse> {
  const response = await apiClient
    .get("classification-rules/categories", {})
    .json<ClassificationRuleCategoriesResponse>();

  return classificationRuleCategoriesResponseSchema.parse(response);
}
